import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Printer, ExternalLink, PackageSearch } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { fetchSessionReport, fetchSessionPdf, downloadBlob, printBlob, openBlob } from "../../lib/cashReportPdf";
import { CashReportSummary } from "./CashReportSummary";
import { CashReportProductsTable } from "./CashReportProductsTable";
import { CashReportStockMovements } from "./CashReportStockMovements";

export const CashCloseReportDialog = ({ sessionId, justClosed, onOpenChange, headers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [showMoves, setShowMoves] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    setData(null); setShowMoves(false); setLoading(true);
    fetchSessionReport(sessionId, headers).then(setData).catch((e) => toast.error(e.response?.data?.detail || "Erro ao gerar relatório")).finally(() => setLoading(false));
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const withPdf = async (action, fn) => {
    setBusy(action);
    try { fn(await fetchSessionPdf(sessionId, headers)); }
    catch { toast.error("Erro ao gerar PDF"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={!!sessionId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto [&>*]:min-w-0" data-testid="cash-close-report-dialog">
        <DialogHeader>
          <DialogTitle>{justClosed ? "Caixa fechada — Relatório de fecho" : "Relatório de caixa"}</DialogTitle>
          <DialogDescription>Produtos vendidos por categoria, stock inicial e final, pagamentos e conferência de numerário. Imprima ou transfira o PDF.</DialogDescription>
        </DialogHeader>
        {loading && <p className="py-10 text-center text-muted-foreground">A gerar relatório…</p>}
        {data && (
          <div className="space-y-4 min-w-0">
            <CashReportSummary data={data} />
            <CashReportProductsTable report={data.report} />
            <p className="text-[11px] text-muted-foreground">Stock final = stock inicial − quantidade vendida + outras entradas/saídas (compras, devoluções, cancelamentos, perdas, ajustes) + pendentes/outro caixa (pedidos ainda em aberto neste caixa, ou pagos aqui mas lançados em caixa anterior). “—” = artigo sem controlo de stock.</p>
            <Button variant="ghost" size="sm" onClick={() => setShowMoves((v) => !v)} data-testid="cash-report-toggle-moves"><PackageSearch className="w-4 h-4 mr-1" /> {showMoves ? "Ocultar" : "Ver"} movimentos de stock para conferência ({data.report?.totals?.stock_movements || 0})</Button>
            {showMoves && <CashReportStockMovements report={data.report} />}
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cash-report-close-btn">Fechar</Button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!data || !!busy} onClick={() => withPdf("open", openBlob)} data-testid="cash-report-open-pdf-btn"><ExternalLink className="w-4 h-4 mr-1" /> Abrir PDF</Button>
            <Button variant="outline" disabled={!data || !!busy} onClick={() => withPdf("print", printBlob)} data-testid="cash-report-print-btn"><Printer className="w-4 h-4 mr-1" /> {busy === "print" ? "A preparar…" : "Imprimir"}</Button>
            <Button disabled={!data || !!busy} onClick={() => withPdf("download", (b) => downloadBlob(b, `fecho-caixa-${sessionId}.pdf`))} data-testid="cash-report-download-btn"><Download className="w-4 h-4 mr-1" /> {busy === "download" ? "A gerar…" : "Transferir PDF"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
